import { Box, type BoxProps } from "@chakra-ui/react";
import type { PropsWithChildren } from "react";

import {
  EdgePrimitive,
  GlowPrimitive
} from "@/design-system/primitives";
import type { PrimitiveTone } from "@/design-system/tokens";
import { MotionBox } from "@/shared/animations/MotionBox";

interface NvpCardProps extends BoxProps {
  readonly tone?: PrimitiveTone;
  readonly interactive?: boolean;
  readonly showGlow?: boolean;
}

export function NvpCard({
  children,
  tone = "blue",
  interactive = false,
  showGlow = false,
  ...props
}: PropsWithChildren<NvpCardProps>) {
  const toneBorder = {
    blue: "rgba(55,213,255,0.5)",
    pink: "rgba(255,79,216,0.52)",
    yellow: "rgba(255,216,77,0.52)",
    violet: "rgba(155,92,255,0.52)",
    green: "rgba(50,243,139,0.48)"
  }[tone];
  const toneGlow = {
    blue: "rgba(55,213,255,0.22)",
    pink: "rgba(255,79,216,0.24)",
    yellow: "rgba(255,216,77,0.2)",
    violet: "rgba(155,92,255,0.24)",
    green: "rgba(50,243,139,0.18)"
  }[tone];

  return (
    <MotionBox
      {...(interactive ? { whileHover: { y: -8, scale: 1.01 } } : {})}
      position="relative"
      overflow="hidden"
      border="1px solid"
      borderColor={toneBorder}
      borderRadius="8px"
      bg="linear-gradient(145deg, rgba(18,22,52,0.9), rgba(8,8,28,0.76) 52%, rgba(18,8,38,0.86))"
      boxShadow={`0 28px 90px rgba(0,0,0,0.42), 0 0 44px ${toneGlow}, inset 0 1px 0 rgba(255,255,255,0.12)`}
      {...props}
    >
      <Box
        aria-hidden="true"
        position="absolute"
        inset="0"
        bg="radial-gradient(circle at 78% 12%, rgba(255,255,255,0.16), transparent 22%), radial-gradient(circle at 12% 90%, rgba(55,213,255,0.08), transparent 34%)"
        pointerEvents="none"
      />
      {showGlow ? (
        <GlowPrimitive tone={tone} top="-60px" right="-40px" h="150px" w="150px" />
      ) : null}
      <EdgePrimitive tone={tone} top="0" left="0" right="0" />
      <Box position="relative" h="100%" w="100%">
        {children}
      </Box>
    </MotionBox>
  );
}
